import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import { getRequest, postRequest, putRequest, deleteRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL, PUT_URL, DEL_URL } from "Includes/urls";
import { AddCircleOutlineOutlined } from "@mui/icons-material";

function ChecklistList(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "" });
  const [editId, setEditId] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignChecklistId, setAssignChecklistId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [searchCompany, setSearchCompany] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  useEffect(() => {
    if (assignOpen) {
      const timer = setTimeout(() => {
        fetchCompanies(searchCompany);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [assignOpen, searchCompany]);

  const fetchCompanies = async (search = '') => {
    try {
      const res = await getRequest(GET_URL.company.api, { search, limit: 30 }, props);
      setCompanies(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch(e) { }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getRequest(GET_URL.checklist_items.api, {}, props);
      if (res && res.data) {
        setRows(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.id);
      setFormData({ name: item.name || "" });
    } else {
      setEditId(null);
      setFormData({ name: "" });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      Swal.fire("Error", "Checklist item name is required", "error");
      return;
    }
    const payload = { name: formData.name };

    setLoading(true);
    try {
      if (editId) {
        await putRequest(`${PUT_URL.checklist_items.api}${editId}/`, payload, props);
        Swal.fire("Success", "Checklist item updated successfully", "success");
      } else {
        await postRequest(POST_URL.checklist_items.api, payload, props);
        Swal.fire("Success", "Checklist item created successfully", "success");
      }
      handleClose();
      fetchData();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Something went wrong", "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await deleteRequest(`${DEL_URL.checklist_items.api}${id}/`, {}, props);
          Swal.fire('Deleted!', 'Checklist item has been deleted.', 'success');
          fetchData();
        } catch (e) {
          console.error(e);
          Swal.fire("Error", "Failed to delete checklist item", "error");
        }
        setLoading(false);
      }
    });
  };

  const handleAssignOneSubmit = async () => {
    if (selectedCompanies.length === 0 || !assignChecklistId) {
      Swal.fire("Error", "Please select at least one institute", "error");
      return;
    }
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const company of selectedCompanies) {
      try {
        const res = await postRequest(POST_URL.company_checklist_items.api, {
          company: company.id,
          checklist_item: assignChecklistId,
          is_done: false
        }, props);
        if (res && res.status === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
      }
    }
    if (successCount > 0) {
      Swal.fire("Success", `Assigned to ${successCount} institute(s)${failCount > 0 ? `, ${failCount} failed (may already be assigned)` : ''}`, "success");
    } else {
      Swal.fire("Error", "Failed to assign (may already be assigned)", "error");
    }
    setAssignOpen(false);
    setAssignChecklistId(null);
    setSelectedCompanies([]);
    setSearchCompany('');
    setLoading(false);
  };

  const handleAssignAllSubmit = async () => {
    if (selectedCompanies.length === 0) {
      Swal.fire("Error", "Please select at least one institute", "error");
      return;
    }
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const company of selectedCompanies) {
      try {
        const res = await postRequest(POST_URL.company_checklist_items.api + 'assign-all/', {
          company_id: company.id,
        }, props);
        if (res && res.status === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
      }
    }
    if (successCount > 0) {
      Swal.fire("Success", `Assigned to ${successCount} institute(s)${failCount > 0 ? `, ${failCount} failed` : ''}`, "success");
    } else {
      Swal.fire("Error", "Failed to assign", "error");
    }
    setAssignOpen(false);
    setAssignChecklistId(null);
    setSelectedCompanies([]);
    setSearchCompany('');
    setLoading(false);
  };

  const columns = [
    { field: "name", headerName: "Checklist Item", flex: 2 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Button size="small" color="secondary" onClick={() => { setAssignChecklistId(params.row.id); setAssignOpen(true); }}>Assign</Button>
          <Button size="small" onClick={() => handleOpen(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>Delete</Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">Client Tracking - Checklist</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => { setAssignChecklistId(null); setAssignOpen(true); }}
          >
            Assign All to Institute
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={() => handleOpen()}
          >
            Add Checklist Item
          </Button>
        </Box>
      </Box>
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Checklist Item" : "Create Checklist Item"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Checklist Item Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={() => { setAssignOpen(false); setAssignChecklistId(null); setSelectedCompanies([]); }} fullWidth maxWidth="sm">
        <DialogTitle>{assignChecklistId ? "Assign Checklist Item to Institutes" : "Assign All Checklist Items to Institutes"}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            multiple
            options={companies}
            getOptionLabel={(option) => option.company_name || option.name || ""}
            value={selectedCompanies}
            onChange={(e, val) => setSelectedCompanies(val)}
            onInputChange={(e, val) => setSearchCompany(val)}
            renderInput={(params) => <TextField {...params} label="Select Institutes" margin="normal" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignOpen(false); setAssignChecklistId(null); setSelectedCompanies([]); }}>Cancel</Button>
          <Button
            onClick={assignChecklistId ? handleAssignOneSubmit : handleAssignAllSubmit}
            variant="contained"
            disabled={loading || selectedCompanies.length === 0}
          >
            {assignChecklistId ? "Assign" : "Assign All"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChecklistList;
